import { useCallback, useEffect, useState } from 'react'

export const ELIGIBILITY_TIMEOUT_MS = 10 * 60 * 1000

const storageKey = (farmId: string) => `isRunningEligibility_${farmId}`

// Storage is unreachable in private or partitioned browsing contexts and throws on write once the
// quota is full. A check that cannot be remembered is not a check that failed, so every access
// degrades to "nothing stored" rather than surfacing to the caller — otherwise the storage error
// reaches processLoading and reports an already-enqueued detection as a failed start.
const readDeadline = (farmId: string): number | null => {
  try {
    const stored = localStorage.getItem(storageKey(farmId))
    if (!stored) return null

    const { timestamp } = JSON.parse(stored) as { timestamp?: unknown }
    return typeof timestamp === 'number' && Number.isFinite(timestamp)
      ? timestamp + ELIGIBILITY_TIMEOUT_MS
      : null
  } catch {
    return null
  }
}

const clearDeadline = (farmId: string) => {
  try {
    localStorage.removeItem(storageKey(farmId))
  } catch {
    /* nothing to reclaim when storage is unreachable */
  }
}

const writeDeadline = (farmId: string, startedAt: number) => {
  try {
    localStorage.setItem(storageKey(farmId), JSON.stringify({ timestamp: startedAt }))
  } catch {
    /* the check still started; it just will not survive a reload */
  }
}

// The deadline is tracked together with the farm it belongs to so the two can never describe
// different farms: releasing an expired check deletes the key of the farm whose deadline ran out,
// not whichever farm happens to be selected when the timer finally fires.
type RunningCheck = { farmId: string; expiresAt: number }

export const useEligibilityStatus = (farmId?: string) => {
  const [running, setRunning] = useState<RunningCheck | null>(null)

  useEffect(() => {
    if (!farmId) {
      setRunning(null)
      return
    }

    const expiresAt = readDeadline(farmId)
    // Expiry is resolved here instead of being left to the timer effect: committing a deadline that
    // already elapsed would report the check as running for a frame, and MUI puts pointer-events
    // none on the disabled button, so a click landing in that frame is swallowed.
    if (expiresAt === null || expiresAt <= Date.now()) {
      clearDeadline(farmId)
      setRunning(null)
      return
    }

    setRunning({ farmId, expiresAt })
  }, [farmId])

  useEffect(() => {
    if (!running) return undefined

    const timer = setTimeout(() => {
      clearDeadline(running.farmId)
      setRunning(current => (current === running ? null : current))
    }, running.expiresAt - Date.now())

    return () => clearTimeout(timer)
  }, [running])

  const startEligibilityCheck = useCallback(() => {
    if (!farmId) return

    const startedAt = Date.now()
    writeDeadline(farmId, startedAt)
    setRunning({ farmId, expiresAt: startedAt + ELIGIBILITY_TIMEOUT_MS })
  }, [farmId])

  const clearEligibilityCheck = useCallback(() => {
    if (!farmId) return

    clearDeadline(farmId)
    setRunning(null)
  }, [farmId])

  return {
    isRunningEligibility: running !== null && running.farmId === farmId,
    startEligibilityCheck,
    clearEligibilityCheck,
  }
}
